import { Fragment, type ReactNode } from 'react';
import { Button } from 'react-bootstrap';
import { useSampleDetailActionLabels } from '../../hooks/useSampleDetailActionLabels';
import TextContentSep from '../common/TextContentSep';

export type SampleDetailHeroActionsProps = {
  canManage: boolean;
  canAddUsage?: boolean;
  readOnlyTitle: string;
  backLabel: string;
  onAddUsage?: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onShare?: () => void;
  onBack: () => void;
};

/**
 * 詳細フルページのヒーロー操作。表示・aria は短ラベル、完全文は title。
 * ボタン間のみ TextContentSep（先頭に置かない）。
 */
export default function SampleDetailHeroActions({
  canManage,
  canAddUsage = false,
  readOnlyTitle,
  backLabel,
  onAddUsage,
  onEdit,
  onDelete,
  onShare,
  onBack,
}: SampleDetailHeroActionsProps) {
  const {
    usageLabel,
    usageShort,
    editLabel,
    deleteLabel,
    editTitle,
    deleteTitle,
    shareShort,
    shareTitle,
    backShort,
  } = useSampleDetailActionLabels();

  const actions: Array<{ key: string; node: ReactNode }> = [];
  if (canManage && canAddUsage && onAddUsage) {
    actions.push({
      key: 'usage',
      node: (
        <Button
          variant="light"
          size="sm"
          className="sample-detail-hero-btn sample-detail-hero-btn--primary"
          onClick={onAddUsage}
          title={usageLabel}
          aria-label={usageShort}
        >
          <i className="fas fa-notes-medical" aria-hidden="true" />
          <span className="sample-detail-hero-btn-label">{usageShort}</span>
        </Button>
      ),
    });
  }
  if (canManage) {
    actions.push({
      key: 'edit',
      node: (
        <Button
          variant="light"
          size="sm"
          className="sample-detail-hero-btn sample-detail-hero-btn--icon"
          onClick={onEdit}
          title={editTitle}
          aria-label={editLabel}
        >
          <i className="fas fa-pen" aria-hidden="true" />
          <span className="sample-detail-hero-btn-label">{editLabel}</span>
        </Button>
      ),
    });
    actions.push({
      key: 'delete',
      node: (
        <Button
          variant="light"
          size="sm"
          className="sample-detail-hero-btn sample-detail-hero-btn--danger sample-detail-hero-btn--icon"
          onClick={onDelete}
          title={deleteTitle}
          aria-label={deleteLabel}
        >
          <i className="fas fa-trash-alt" aria-hidden="true" />
          <span className="sample-detail-hero-btn-label">{deleteLabel}</span>
        </Button>
      ),
    });
  }
  if (onShare) {
    actions.push({
      key: 'share',
      node: (
        <Button
          variant="light"
          size="sm"
          className="sample-detail-hero-btn sample-detail-hero-btn--icon"
          onClick={onShare}
          disabled={!canManage}
          title={!canManage ? readOnlyTitle : shareTitle}
          aria-label={shareShort}
        >
          <i className="fas fa-share-alt" aria-hidden="true" />
          <span className="sample-detail-hero-btn-label">{shareShort}</span>
        </Button>
      ),
    });
  }
  actions.push({
    key: 'back',
    node: (
      <Button
        variant="light"
        size="sm"
        className="sample-detail-hero-btn sample-detail-hero-btn--icon"
        onClick={onBack}
        title={backLabel}
        aria-label={backShort}
      >
        <i className="fas fa-arrow-left" aria-hidden="true" />
        <span className="sample-detail-hero-btn-label">{backShort}</span>
      </Button>
    ),
  });

  return (
    <div className="sample-detail-hero-actions">
      {actions.map((action, index) => (
        <Fragment key={action.key}>
          {index > 0 ? <TextContentSep /> : null}
          {action.node}
        </Fragment>
      ))}
    </div>
  );
}
